import axios from 'axios';
import {
  isBillingReconciliationMockMode,
  listPayload,
  mockCredits,
  mockFileHealth,
  mockLifecycleAlerts,
  requestWithMockFallback,
  uniqueCatalogRows,
} from './billingReconciliationMockApi.js';
export {
  createBillingReconciliationDpeAction,
  deleteBillingReconciliationDpeAction,
  listBillingReconciliationDpeActions,
  sendBillingReconciliationDpeActions,
  updateBillingReconciliationDpeAction,
} from './billingReconciliationDpeActionsApi.js';
export {
  getBillingReconciliationBacContext,
  getBillingReconciliationBacContextBatch,
  getBillingReconciliationOperatorContext,
} from './billingReconciliationBacContextApi.js';
export { resetBillingReconciliationVarianceReviews } from './billingReconciliationReviewApi.js';
export {
  listBillingReconciliationAppSheetBacAccounts,
  listBillingReconciliationAppSheetRows,
  searchBillingReconciliationAppSheetAccounts,
} from './billingReconciliationAppSheetApi.js';
export { refreshBillingReconciliationVarianceClassifications } from './billingReconciliationVarianceRefreshApi.js';

export { isBillingReconciliationMockMode };

const BASE_URL = '/api/billing-reconciliation';

const cleanParams = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
);
const requestConfig = (params = {}, signal = undefined) => ({
  params: cleanParams(params),
  ...(signal ? { signal } : {}),
});
const encoded = (value) => encodeURIComponent(String(value));
const makeIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `billing-recon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const listBillingReconciliationRuns = async ({ signal, ...params } = {}) => requestWithMockFallback(
  async () => {
    const { data } = await axios.get(`${BASE_URL}/runs`, requestConfig(params, signal));
    return data;
  },
  () => listPayload([]),
);
export const getBillingReconciliationRun = async (id) => {
  if (!id) throw new Error('run id is required');
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(id)}`);
  return data;
};

export const createBillingReconciliationRun = async ({
  program,
  serviceMonth,
  gmFile,
  cpqFile,
  cpqSourceMode,
  idempotencyKey,
} = {}) => {
  if (!program) throw new Error('program is required');
  if (!serviceMonth) throw new Error('serviceMonth is required');

  const headers = { 'Idempotency-Key': idempotencyKey || makeIdempotencyKey() };
  let payload;
  if (gmFile || cpqFile) {
    payload = new FormData();
    payload.append('program', program);
    payload.append('serviceMonth', serviceMonth);
    if (cpqSourceMode) payload.append('cpqSourceMode', cpqSourceMode);
    if (gmFile) payload.append('gmFile', gmFile);
    if (cpqFile) payload.append('cpqFile', cpqFile);
  } else {
    payload = { program, serviceMonth, cpqSourceMode };
  }

  const { data } = await axios.post(`${BASE_URL}/runs`, payload, { headers });
  return data;
};

const backfillFormData = ({ program, serviceMonth, originalFile, finalFile } = {}) => {
  if (!program) throw new Error('program is required');
  if (!serviceMonth) throw new Error('serviceMonth is required');
  if (!originalFile) throw new Error('originalFile is required');
  if (!finalFile) throw new Error('finalFile is required');
  const payload = new FormData();
  payload.append('program', program);
  payload.append('serviceMonth', serviceMonth);
  payload.append('originalFile', originalFile);
  payload.append('finalFile', finalFile);
  return payload;
};

export const previewBillingReconciliationHistoricalBackfill = async (input = {}) => {
  const { data } = await axios.post(`${BASE_URL}/backfill/preview`, backfillFormData(input));
  return data;
};

export const importBillingReconciliationHistoricalBackfill = async ({
  idempotencyKey,
  ...input
} = {}) => {
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
  const { data } = await axios.post(`${BASE_URL}/backfill/import`, backfillFormData(input), { headers });
  return data;
};

export const deleteBillingReconciliationRun = async ({ id, confirmToken } = {}) => {
  if (!id) throw new Error('run id is required');
  const { data } = await axios.delete(`${BASE_URL}/runs/${encoded(id)}`, {
    data: cleanParams({ confirmToken }),
  });
  return data;
};

export const listBillingReconciliationVariances = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/variances`, requestConfig(params, signal));
  return data;
};

export const listBillingReconciliationGmRows = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/gm-rows`, requestConfig(params, signal));
  return Array.isArray(data) ? listPayload(data) : data;
};

export const listBillingReconciliationHistoricalFinalGmRows = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/historical-final-gm-rows`, requestConfig(params, signal));
  return Array.isArray(data) ? listPayload(data) : data;
};

export const listBillingReconciliationCpqRows = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/cpq-rows`, requestConfig(params, signal));
  return Array.isArray(data) ? listPayload(data) : data;
};

export const listBillingReconciliationDpeRows = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return { products: [], enrollment: null, source: {} };
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/dpe-rows`, requestConfig(params, signal));
  return data;
};

export const updateBillingReconciliationVarianceReview = async ({ runId, bac, varianceId, patch, ifUpdatedAt } = {}) => {
  const id = varianceId || bac;
  if (!runId) throw new Error('runId is required');
  if (!id) throw new Error('variance id or bac is required');
  const { data } = await axios.patch(`${BASE_URL}/runs/${encoded(runId)}/variances/${encoded(id)}`, cleanParams({
    ...(patch || {}),
    ifUpdatedAt,
  }));
  return data;
};

export const getBillingReconciliationVarianceCompletionReadiness = async ({ varianceId, runId, signal } = {}) => {
  if (!varianceId) return { ready: true, blockers: [] };
  const { data } = await axios.get(`${BASE_URL}/variances/${encoded(varianceId)}/completion-readiness`, {
    params: cleanParams({ runId }),
    ...(signal ? { signal } : {}),
  });
  return data;
};

export const listBillingReconciliationNotes = async ({ varianceId } = {}) => {
  if (!varianceId) return [];
  const { data } = await axios.get(`${BASE_URL}/variances/${encoded(varianceId)}/notes`);
  return data;
};

export const createBillingReconciliationNote = async ({ varianceId, text, isDraft = false } = {}) => {
  if (!varianceId) throw new Error('varianceId is required');
  const { data } = await axios.post(`${BASE_URL}/variances/${encoded(varianceId)}/notes`, { text, isDraft });
  return data;
};

export const updateBillingReconciliationNote = async ({ id, patch = {}, ifUpdatedAt } = {}) => {
  if (!id) throw new Error('note id is required');
  const { data } = await axios.patch(`${BASE_URL}/notes/${encoded(id)}`, cleanParams({
    ...patch,
    ifUpdatedAt,
  }));
  return data;
};

export const deleteBillingReconciliationNote = async ({ id, ifUpdatedAt } = {}) => {
  if (!id) throw new Error('note id is required');
  const { data } = await axios.delete(`${BASE_URL}/notes/${encoded(id)}`, {
    data: cleanParams({ ifUpdatedAt }),
  });
  return data;
};

export const listBillingReconciliationVarianceActions = async ({ varianceId, signal } = {}) => {
  if (!varianceId) return [];
  const { data } = signal
    ? await axios.get(`${BASE_URL}/variances/${encoded(varianceId)}/actions`, { signal })
    : await axios.get(`${BASE_URL}/variances/${encoded(varianceId)}/actions`);
  return data;
};

export const createBillingReconciliationVarianceAction = async ({ varianceId, action } = {}) => {
  if (!varianceId) throw new Error('varianceId is required');
  const { data } = await axios.post(`${BASE_URL}/variances/${encoded(varianceId)}/actions`, {
    ...(action || {}),
    reopenReviewedVariance: true,
  });
  return data;
};

export const updateBillingReconciliationVarianceAction = async ({ id, patch = {}, ifUpdatedAt = null } = {}) => {
  if (!id) throw new Error('variance action id is required');
  const { data } = await axios.patch(`${BASE_URL}/variance-actions/${encoded(id)}`, cleanParams({
    ...(patch || {}),
    ifUpdatedAt,
    reopenReviewedVariance: true,
  }));
  return data;
};

export const deleteBillingReconciliationVarianceAction = async ({ id, ifUpdatedAt = null } = {}) => {
  if (!id) throw new Error('variance action id is required');
  const { data } = await axios.delete(`${BASE_URL}/variance-actions/${encoded(id)}`, {
    data: cleanParams({ ifUpdatedAt, reopenReviewedVariance: true }),
  });
  return data;
};

export const saveBillingReconciliationVarianceActions = async ({ varianceId, expectedActionVersions = null } = {}) => {
  if (!varianceId) throw new Error('varianceId is required');
  const payload = cleanParams({ expectedActionVersions });
  const { data } = Object.keys(payload).length
    ? await axios.post(`${BASE_URL}/variances/${encoded(varianceId)}/actions/save`, payload)
    : await axios.post(`${BASE_URL}/variances/${encoded(varianceId)}/actions/save`);
  return data;
};

export const resolveBillingReconciliationVarianceActions = async ({ varianceId } = {}) => {
  if (!varianceId) throw new Error('varianceId is required');
  const { data } = await axios.post(`${BASE_URL}/variances/${encoded(varianceId)}/actions/resolve`);
  return data;
};

export const resetBillingReconciliationVarianceActions = async ({ varianceId, statuses, expectedActionVersions = null } = {}) => {
  if (!varianceId) throw new Error('varianceId is required');
  const { data } = await axios.delete(`${BASE_URL}/variances/${encoded(varianceId)}/actions`, {
    data: cleanParams({ statuses: statuses || ['draft', 'inherited_pending', 'saved', 'resolved'], expectedActionVersions, reopenReviewedVariance: true }),
  });
  return data;
};

export const getBillingReconciliationExportPreview = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return { rows: [], summary: { total: 0, draftCount: 0, byLane: {}, byMutation: {} } };
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/export-preview`, requestConfig(params, signal));
  return data;
};

export const getBillingReconciliationActionSummary = async ({ runId, signal } = {}) => {
  if (!runId) return { total: 0, draftCount: 0, byLane: {}, byMutation: {}, byStatus: {} };
  const { data } = signal
    ? await axios.get(`${BASE_URL}/runs/${encoded(runId)}/actions-summary`, { signal })
    : await axios.get(`${BASE_URL}/runs/${encoded(runId)}/actions-summary`);
  return data;
};

export const listBillingReconciliationExports = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return { rows: [], total: 0 };
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/exports`, requestConfig(params, signal));
  return data;
};

export const downloadBlobResponse = (response, fallbackName) => {
  const blob = new Blob([response.data], {
    type: response.headers?.['content-type'] || 'application/octet-stream',
  });
  const disposition = response.headers?.['content-disposition'] || '';
  const fileName = disposition.match(/filename="([^"]+)"/)?.[1] || fallbackName;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadBillingReconciliationExport = async ({ runId, lane = 'gm_correction' } = {}) => {
  if (!runId) throw new Error('runId is required');
  const response = await axios.post(`${BASE_URL}/runs/${encoded(runId)}/retained-export.xlsx`, { lane }, { responseType: 'blob' });
  downloadBlobResponse(response, `billing-reconciliation-${lane}.xlsx`);
};

export const downloadBillingReconciliationActionSummaryCsv = async ({ runId, lane = null } = {}) => {
  if (!runId) throw new Error('runId is required');
  const response = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/actions-summary.csv`, {
    params: cleanParams({ lane }),
    responseType: 'blob',
  });
  downloadBlobResponse(response, 'billing-reconciliation-action-summary.csv');
};

export const downloadBillingReconciliationExportLineageCsv = async ({ runId, lane = null } = {}) => {
  if (!runId) throw new Error('runId is required');
  const response = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/export-lineage.csv`, {
    params: cleanParams({ lane }),
    responseType: 'blob',
  });
  downloadBlobResponse(response, 'billing-reconciliation-export-lineage.csv');
};

export const downloadBillingReconciliationExportArtifact = async ({ runId, exportId, kind = 'workbook' } = {}) => {
  if (!runId || !exportId) throw new Error('runId and exportId are required');
  const response = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/exports/${encoded(exportId)}/${encoded(kind)}`, { responseType: 'blob' });
  downloadBlobResponse(response, `billing-reconciliation-${kind}`);
};

export const downloadBillingReconciliationExportBundle = async ({ runId } = {}) => {
  if (!runId) throw new Error('runId is required');
  const response = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/export-bundle.zip`, { responseType: 'blob' });
  downloadBlobResponse(response, 'billing-reconciliation-export-bundle.zip');
};

export const listBillingReconciliationFileHealth = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  return requestWithMockFallback(
    async () => {
      const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/file-health`, requestConfig({ openOnly: true, ...params }, signal));
      return data;
    },
    mockFileHealth,
  );
};

export const summarizeBillingReconciliationFileHealth = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return { total: 0, open: 0, addressed: 0, byState: {}, byTier: {}, byCode: [], byBac: [] };
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/file-health/summary`, requestConfig(params, signal));
  return data;
};

export const refreshBillingReconciliationFileHealth = async ({ runId } = {}) => {
  if (!runId) throw new Error('runId is required');
  const { data } = await axios.post(`${BASE_URL}/runs/${encoded(runId)}/file-health/refresh`);
  return data;
};

export const reviewBillingReconciliationFileHealth = async ({
  id,
  runId,
  findingFingerprint,
  broadAlertKey,
  materialFactsHash,
  state = 'addressed',
  note = '',
  reviewNote = '',
} = {}) => {
  if (runId && (id || findingFingerprint || (broadAlertKey && materialFactsHash))) {
    const { data } = await axios.patch(`${BASE_URL}/runs/${encoded(runId)}/file-health/review`, cleanParams({
      id,
      findingFingerprint,
      broadAlertKey,
      materialFactsHash,
      state,
      note,
      reviewNote,
    }));
    return data;
  }
  if (!id) throw new Error('file health finding id is required');
  const { data } = await axios.patch(`${BASE_URL}/file-health/${encoded(id)}/review`, cleanParams({ state, note, reviewNote }));
  return data;
};

export const bulkReviewBillingReconciliationFileHealth = async ({
  runId,
  ids,
  findingIds,
  code,
  codes,
  bac,
  state = 'addressed',
  note = '',
  reviewNote = '',
} = {}) => {
  if (!runId) throw new Error('runId is required');
  const { data } = await axios.patch(`${BASE_URL}/runs/${encoded(runId)}/file-health/bulk-review`, cleanParams({
    ids,
    findingIds,
    code,
    codes,
    bac,
    state,
    note,
    reviewNote,
  }));
  return data;
};

export const listBillingReconciliationSuggestedMatches = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return { rows: [], total: 0 };
  const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/suggested-matches`, requestConfig(params, signal));
  return data;
};

export const confirmBillingReconciliationSuggestedMatch = async ({ runId, ruleId, varianceId, note } = {}) => {
  if (!runId || !ruleId || !varianceId) throw new Error('runId, ruleId, and varianceId are required');
  const { data } = await axios.post(`${BASE_URL}/runs/${encoded(runId)}/suggested-matches/${encoded(ruleId)}/confirm`, cleanParams({ varianceId, note }));
  return data;
};

export const rejectBillingReconciliationSuggestedMatch = async ({ runId, ruleId, varianceId, status = 'rejected', note } = {}) => {
  if (!runId || !ruleId) throw new Error('runId and ruleId are required');
  const { data } = await axios.post(`${BASE_URL}/runs/${encoded(runId)}/suggested-matches/${encoded(ruleId)}/reject`, cleanParams({ varianceId, status, note }));
  return data;
};

export const listBillingReconciliationRecurringRules = async ({ signal, ...params } = {}) => {
  const { data } = await axios.get(`${BASE_URL}/recurring-match-rules`, requestConfig(params, signal));
  return data;
};

export const updateBillingReconciliationRecurringRule = async ({ id, patch = {} } = {}) => {
  if (!id) throw new Error('recurring rule id is required');
  const { data } = await axios.patch(`${BASE_URL}/recurring-match-rules/${encoded(id)}`, patch);
  return data;
};

export const promoteBillingReconciliationActionToRecurringRule = async ({ id, auditNote } = {}) => {
  if (!id) throw new Error('variance action id is required');
  const { data } = await axios.post(`${BASE_URL}/variance-actions/${encoded(id)}/promote-recurring`, cleanParams({ auditNote }));
  return data;
};

export const listBillingReconciliationLifecycleAlerts = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  return requestWithMockFallback(
    async () => {
      const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/lifecycle-alerts`, requestConfig(params, signal));
      return data;
    },
    mockLifecycleAlerts,
  );
};

export const updateBillingReconciliationLifecycleAlert = async ({ id, patch } = {}) => {
  if (!id) throw new Error('alert id is required');
  const { data } = await axios.patch(`${BASE_URL}/lifecycle-alerts/${encoded(id)}`, patch || {});
  return data;
};

export const listBillingReconciliationCredits = async ({ runId, signal, ...params } = {}) => {
  if (!runId) return listPayload([]);
  return requestWithMockFallback(
    async () => {
      const { data } = await axios.get(`${BASE_URL}/runs/${encoded(runId)}/credits`, requestConfig(params, signal));
      return data;
    },
    mockCredits,
  );
};

export const createBillingReconciliationCredit = async ({ runId, ...payload } = {}) => {
  if (!runId) throw new Error('runId is required');
  const { data } = await axios.post(`${BASE_URL}/runs/${encoded(runId)}/credits`, cleanParams(payload));
  return data;
};

export const listBillingReconciliationCatalog = async ({ signal, ...params } = {}) => requestWithMockFallback(
  async () => {
    const { data } = await axios.get(`${BASE_URL}/catalog`, requestConfig(params, signal));
    return data;
  },
  async () => listPayload(await uniqueCatalogRows()),
);

export const listBillingReconciliationNoteTemplates = async ({ signal, ...params } = {}) => {
  const { data } = await axios.get(`${BASE_URL}/note-templates`, requestConfig(params, signal));
  return data;
};

export const updateBillingReconciliationCatalogItem = async ({ id, patch } = {}) => {
  if (!id) throw new Error('catalog item id is required');
  const { data } = await axios.patch(`${BASE_URL}/catalog/${encoded(id)}`, patch || {});
  return data;
};

export const markBillingReconciliationCatalogItemReviewed = async ({ id } = {}) => {
  if (!id) throw new Error('catalog item id is required');
  const { data } = await axios.patch(`${BASE_URL}/catalog/${encoded(id)}/mark-reviewed`);
  return data;
};
